/**
 * /app/events — system events timeline.
 *
 * Annotations layer: every record explains "what changed in the system
 * around this date" so equity curve dips / win-rate jumps stop being
 * mysterious. Admins create + edit; everybody reads.
 *
 * Layout:
 *   Filter bar (type · period) + Add button (admin only)
 *   Chronological list grouped by day, period events span their range
 *   Click a row → expanded description + metadata + delete (admin)
 */
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'

import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { useAuthStore } from '@/stores/auth'
import {
  type EventType,
  type EventsFilters,
  type SystemEvent,
  createEvent,
  deleteEvent,
  useEvents,
} from '@/hooks/useDashboardApi'
import { cn } from '@/lib/cn'
import { timeAgo } from '@/lib/format'

const EVENT_TYPES: EventType[] = [
  'strategy', 'research', 'deploy', 'bug', 'config_change', 'test',
  'market_event', 'incident', 'note',
]

/** Tone palette per type — also used by EquityCurve overlay. Keep in sync. */
export const EVENT_TONE: Record<EventType, {
  bg: string; text: string; line: string
}> = {
  strategy:      { bg: 'bg-pulse-faint',  text: 'text-pulse',  line: 'var(--pulse)'  },
  research:      { bg: 'bg-amber-faint',  text: 'text-amber',  line: 'var(--amber)'  },
  deploy:        { bg: 'bg-sky-faint',    text: 'text-sky',    line: 'var(--sky)'    },
  bug:           { bg: 'bg-rose-faint',   text: 'text-rose',   line: 'var(--rose)'   },
  config_change: { bg: 'bg-indigo-faint', text: 'text-indigo', line: 'var(--indigo)' },
  test:          { bg: 'bg-indigo-faint', text: 'text-indigo', line: 'var(--indigo)' },
  market_event:  { bg: 'bg-amber-faint',  text: 'text-amber',  line: 'var(--amber)'  },
  incident:      { bg: 'bg-fire-faint',   text: 'text-fire',   line: 'var(--fire)'   },
  note:          { bg: 'bg-elevated',     text: 'text-textDim', line: 'var(--text-faint)' },
}

export default function Events() {
  const { t } = useTranslation('app')
  const isAdmin = useAuthStore((s) => s.isAdmin())
  const qc = useQueryClient()

  const [typeFilter, setTypeFilter] = useState<EventType | ''>('')
  const [days, setDays] = useState(90)
  const [showForm, setShowForm] = useState(false)

  const filters: EventsFilters = useMemo(() => {
    const f: EventsFilters = { limit: 500 }
    if (typeFilter) f.type = typeFilter
    if (days > 0) {
      const from = new Date(Date.now() - days * 86400_000).toISOString()
      f.from = from
    }
    return f
  }, [typeFilter, days])

  const { data, isLoading } = useEvents(filters)

  return (
    <div className="p-4 sm:p-6">
      <header className="mb-4 flex items-baseline gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t('events.title')}
          </h1>
          <p className="text-sm text-textDim mt-1">{t('events.subtitle')}</p>
        </div>
        {isAdmin && (
          <Button
            size="sm" variant="primary"
            className="ml-auto"
            onClick={() => setShowForm((x) => !x)}
          >
            {showForm ? t('events.cancel') : t('events.add')}
          </Button>
        )}
      </header>

      {showForm && isAdmin && (
        <AddForm
          onCreated={() => {
            setShowForm(false)
            qc.invalidateQueries({ queryKey: ['events'] })
          }}
        />
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
        <Select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as EventType | '')}
        >
          <option value="">{t('events.filter.type_all')}</option>
          {EVENT_TYPES.map((tt) => (
            <option key={tt} value={tt}>
              {t(`events.type.${tt}`)}
            </option>
          ))}
        </Select>
        <Select
          value={String(days)}
          onChange={(e) => setDays(Number(e.target.value))}
        >
          <option value="7">{t('events.range.days', { count: 7 })}</option>
          <option value="30">{t('events.range.days', { count: 30 })}</option>
          <option value="90">{t('events.range.days', { count: 90 })}</option>
          <option value="365">{t('events.range.days', { count: 365 })}</option>
          <option value="0">{t('events.range.all')}</option>
        </Select>
      </div>

      <section className="bg-ink border border-line">
        {isLoading || !data ? (
          <div className="px-5 py-10 text-center text-textFaint text-sm">—</div>
        ) : data.items.length === 0 ? (
          <div className="px-5 py-10 text-center text-textFaint text-sm">
            {t('events.empty')}
          </div>
        ) : (
          <ul className="divide-y divide-line/40">
            {data.items.map((ev) => (
              <EventRow
                key={ev.id}
                ev={ev}
                isAdmin={isAdmin}
                onDeleted={() => qc.invalidateQueries({ queryKey: ['events'] })}
              />
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}

/* ─── Row ───────────────────────────────────────────────────────── */

function EventRow({
  ev, isAdmin, onDeleted,
}: {
  ev: SystemEvent
  isAdmin: boolean
  onDeleted: () => void
}) {
  const { t } = useTranslation('app')
  const [expanded, setExpanded] = useState(false)
  const tone = EVENT_TONE[ev.type]
  const isPeriod = ev.ts_end != null

  const onDelete = async () => {
    if (!confirm(t('events.confirm_delete'))) return
    try { await deleteEvent(ev.id); onDeleted() } catch { /* toast */ }
  }

  return (
    <li>
      <div
        className="flex items-start gap-3 px-5 py-3 hover:bg-elevated/30 cursor-pointer"
        onClick={() => setExpanded((x) => !x)}
      >
        <span className={cn('pill mt-0.5', tone.bg, tone.text)}>
          {t(`events.type.${ev.type}`)}
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium truncate">
            {ev.metadata?.ref != null && (
              <span className="font-mono text-2xs text-textFaint uppercase mr-2">
                {String(ev.metadata.ref)}
              </span>
            )}
            {ev.title}
          </div>
          <div className="text-xs text-textFaint mt-0.5 font-mono">
            {new Date(ev.ts_start).toLocaleString()}
            {isPeriod && ` → ${new Date(ev.ts_end!).toLocaleString()}`}
            <span className="ml-2">· {timeAgo(ev.ts_start)}</span>
          </div>
        </div>
      </div>
      {expanded && (
        <div className="px-5 pb-4 bg-elevated/20 border-t border-line/40">
          {ev.description && (
            <p className="text-sm text-textDim whitespace-pre-wrap mt-3">
              {ev.description}
            </p>
          )}
          {isAdmin && Object.keys(ev.metadata).length > 0 && (
            <pre className="mt-3 p-2 bg-ink border border-line text-2xs font-mono overflow-x-auto">
              {JSON.stringify(ev.metadata, null, 2)}
            </pre>
          )}
          {isAdmin && (
            <div className="mt-3 flex gap-2">
              <Button size="sm" variant="danger" onClick={onDelete}>
                {t('events.delete')}
              </Button>
            </div>
          )}
        </div>
      )}
    </li>
  )
}

/* ─── Add form (admin only) ─────────────────────────────────────── */

function AddForm({ onCreated }: { onCreated: () => void }) {
  const { t } = useTranslation('app')
  const [type, setType] = useState<EventType>('note')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  // datetime-local inputs use local YYYY-MM-DDTHH:mm — Date() interprets
  // as local; we send ISO with timezone after parse.
  const nowLocal = () => {
    const d = new Date()
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset())
    return d.toISOString().slice(0, 16)
  }
  const [tsStart, setTsStart] = useState<string>(nowLocal())
  const [tsEnd, setTsEnd] = useState<string>('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true); setError(null)
    try {
      await createEvent({
        type,
        title: title.trim(),
        description: description.trim() || undefined,
        ts_start: new Date(tsStart).toISOString(),
        ts_end: tsEnd ? new Date(tsEnd).toISOString() : null,
      })
      setTitle(''); setDescription(''); setTsEnd('')
      onCreated()
    } catch (err) {
      const msg = (err as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail ?? 'unknown_error'
      setError(String(msg))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form
      onSubmit={submit}
      className="mb-4 p-4 bg-ink border border-line space-y-3"
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <Select
          value={type}
          onChange={(e) => setType(e.target.value as EventType)}
        >
          {EVENT_TYPES.map((tt) => (
            <option key={tt} value={tt}>
              {t(`events.type.${tt}`)}
            </option>
          ))}
        </Select>
        <Input
          type="datetime-local"
          value={tsStart}
          onChange={(e) => setTsStart(e.target.value)}
          required
        />
        <Input
          type="datetime-local"
          placeholder={t('events.form.ts_end_optional')}
          value={tsEnd}
          onChange={(e) => setTsEnd(e.target.value)}
        />
      </div>
      <Input
        placeholder={t('events.form.title')}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        required
        minLength={1}
        maxLength={200}
      />
      <textarea
        className="block w-full px-3 py-2 text-sm rounded bg-elevated text-text
                   placeholder:text-textFaint border border-line
                   hover:border-lineHi focus:outline-none focus:border-fire
                   min-h-[80px]"
        placeholder={t('events.form.description')}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />
      {error && (
        <p className="text-xs text-rose">{error}</p>
      )}
      <div className="flex gap-2">
        <Button type="submit" loading={submitting} disabled={!title.trim()}>
          {t('events.form.submit')}
        </Button>
      </div>
    </form>
  )
}
